import { useEffect, useMemo } from "react";
import { Calendar } from "react-date-range";
import { DateTime } from "luxon";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import {
  useAmenityBookingEngine,
  AmenityDayItem,
  BookingAvailabilityType,
  AmenityDayAvailability,
  CalendarTileStatus,
  BookingError,
} from "./useAmenityBookingEngine";
import { useAvailabilities } from "../DailyBooking";
import { AmenityBookingRequest } from "@/types/Bookings";
import { Button } from "../Button";
import { useMediaQuery } from "@/hooks/useMediaQuery";

export interface DateRange {
  startDate: string; // ISO date string
  endDate: string; // ISO date string
}

type Props = {
  booking: AmenityBookingRequest;
  onUpdateBooking: (booking: AmenityBookingRequest) => void;
  displayAvailabilityInDays: number;
  timezone: string;
  onNext: () => void;
};

function ByDayBooking({
  validDates,
  booking,
  onUpdateBooking,
  displayAvailabilityInDays,
  timezone,
  onNext,
}: Props & {
  validDates: string[];
}) {
  const isSmallDevice = useMediaQuery("only screen and (max-width : 768px)");
  // Convert valid dates to AmenityDayItems
  const initialDayItems = useMemo<AmenityDayItem[]>(() => {
    const validDatesSet = new Set(validDates);
    const items: AmenityDayItem[] = [];

    // Create a date range for the calendar (e.g., next 90 days)
    const today = DateTime.now().setZone(timezone).startOf("day");
    const endDate = today.plus({ days: displayAvailabilityInDays - 1 });

    let currentDate = today;
    while (currentDate <= endDate) {
      const key = currentDate.toISODate();
      if (key) {
        const isValid = validDatesSet.has(key);

        items.push({
          key,
          date: currentDate,
          availability: isValid
            ? AmenityDayAvailability.Available
            : AmenityDayAvailability.Unavailable,
          isSelected: false,
          viewStatus: CalendarTileStatus.Available,
        });
      }
      currentDate = currentDate.plus({ days: 1 });
    }

    return items;
  }, [validDates, timezone]);

  const { dayItems, error, selectedKeys, selectDay, resetSelections } =
    useAmenityBookingEngine({
      initialDayItems,
      availabilityType: BookingAvailabilityType.ByDay,
      minimumDuration: undefined,
      maximumDuration: undefined,
      onSelectionChange: (keys) => {
        if (keys.length === 0) {
          onUpdateBooking({ ...booking, startTime: null, endTime: null });
          return;
        }
        // Single date selection
        const dayKey = keys[0];
        const startTime = DateTime.fromISO(dayKey, { zone: timezone })
          .startOf("day")
          .toUTC()
          .toISO();

        const endTime = DateTime.fromISO(dayKey, { zone: timezone })
          .plus({ days: 1 })
          .startOf("day")
          .toUTC()
          .toISO();

        onUpdateBooking({ ...booking, startTime, endTime });
      },
    });

  // Convert dayItems to disabled dates for react-date-range
  const disabledDates = useMemo(() => {
    return Object.values(dayItems)
      .filter(
        (item) =>
          item.availability === AmenityDayAvailability.Unavailable ||
          item.viewStatus === CalendarTileStatus.Unavailable
      )
      .map(
        (item) =>
          // Convert to browser's local timezone using date components
          new Date(item.date.year, item.date.month - 1, item.date.day)
      );
  }, [dayItems]);

  // Selected date for Calendar component
  const selectedDate = useMemo(() => {
    if (selectedKeys.length === 0) return undefined;
    const item = dayItems[selectedKeys[0]];
    if (!item) return undefined;
    return new Date(item.date.year, item.date.month - 1, item.date.day);
  }, [selectedKeys, dayItems]);

  // Single date selection handler
  const handleSelect = (date: Date) => {
    if (!date) return;
    const dateTime = DateTime.fromObject(
      {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
      },
      { zone: timezone }
    ).startOf("day");
    const key = dateTime.toISODate();
    if (key) selectDay(key);
  };

  const maxDate = useMemo(() => {
    const sortedItems = Object.values(dayItems).sort(
      (a, b) => b.date.toMillis() - a.date.toMillis()
    );
    const lastAvailable = sortedItems.find(
      (item) => item.availability === AmenityDayAvailability.Available
    );
    if (lastAvailable) {
      return new Date(
        lastAvailable.date.year,
        lastAvailable.date.month - 1,
        lastAvailable.date.day
      );
    }
    const fallback = DateTime.now()
      .setZone(timezone)
      .plus({ days: 90 })
      .startOf("day");
    return new Date(fallback.year, fallback.month - 1, fallback.day);
  }, [dayItems, timezone]);

  return (
    <div className="daily-booking">
      <style>{`
        .rdrDay.rdrDayPassive {
          visibility: hidden;
          pointer-events: none;
        }
        .rdrDay.rdrDayPassive .rdrDayNumber span {
          visibility: hidden;
        }
        .rdrDayDisabled, .rdrDayDisabled .rdrInRange {
          background-color: white !important;
        }
        .rdrDay.rdrDayDisabled .rdrDayNumber span {
          color: #ccc !important;
        }
        .rdrDay.rdrDayDisabled:hover {
          background-color: white !important;
        }
        .rdrDayNumber span {
          color: black;
        }
       
      `}</style>
      <div className="flex justify-center">
        <Calendar
          onChange={handleSelect}
          date={selectedDate}
          minDate={new Date()}
          maxDate={maxDate}
          months={isSmallDevice ? 1 : 2}
          direction="horizontal"
          disabledDates={disabledDates}
          showMonthAndYearPickers={false}
          showDateDisplay={false}
        />
      </div>

      {error !== BookingError.None && (
        <div className="mt-4 text-red-600">
          {error === BookingError.CheckOutOnlyError &&
            "This date is only available for check-out"}
          {error === BookingError.CompositionError &&
            "An error occurred. Please try again."}
        </div>
      )}

      <div className="mt-4 flex justify-between items-center">
        <button
          className="inline-flex items-center p-1 text-sm hover:bg-gray-50"
          onClick={resetSelections}
        >
          Clear Dates
        </button>
        <Button
          color="primary"
          size="small"
          disabled={!booking.startTime}
          onClick={onNext}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

function Container(props: Props & { amenitySpaceId: number }) {
  const { amenitySpaceId, ...reset } = props;
  const { data: availabilities = [], isLoading } = useAvailabilities(
    amenitySpaceId,
    new Date()
  );

  const validDates = useMemo(() => {
    return availabilities
      .filter(
        (availability) => availability.availabilityType === 1 // Available
      )
      .map((availability) => availability.startTime.toString().split("T")[0]);
  }, [availabilities]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (validDates.length === 0) {
    return <div>No available dates for booking at this time.</div>;
  }

  return <ByDayBooking validDates={validDates} {...reset} />;
}

export default Container;
